import { Area, AreaChart, ResponsiveContainer, Tooltip, TooltipContentProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { format } from 'date-fns';
import { cn } from '@/utils/utils.ts';
import { useTranslation } from 'react-i18next';

export type RangeGranularity = '5min' | 'hourly' | 'daily' | 'monthly';

const formatValueAndUnit = (inputNumber: number | undefined) => {
  if (!inputNumber)
    return {
      value: '0',
      unit: 'kWh',
    };

  const isValueBelow1k = inputNumber < 1000;

  const value = isValueBelow1k ? inputNumber.toFixed(2) : (inputNumber / 1000).toFixed(2);
  const unit = isValueBelow1k ? 'kWh' : 'MWh';

  return {
    value,
    unit,
  };
};

const calculateBarsPercentage = ({
  left,
  right,
}: {
  left: number | string | undefined;
  right: number | string | undefined;
}) => {
  if (left === 0 && left === 0) return [50, 50];
  if (!left && !right) return [0, 0];
  if (!left) left = 0;
  if (!right) right = 0;

  left = Number(left);
  right = Number(right);

  const total = right + left;

  const leftPercentage = Math.round((left / total) * 100);
  const rightPercentage = Math.round((right / total) * 100);

  return [leftPercentage, rightPercentage];
};

const METRIC_COLORS = {
  PV_PRODUCTION: '#18CF87',
  CONSUMED: '#F76354',
  CONSUMED_FROM_GRID: '#EEBA18',
  FED_TO_GRID: '#8DD76B',
  BATTERY_CHARGE: '#038BA8',
  BATTERY_DISCHARGE: '#AC44FB',
} as const;

export function EnergySummaryCards({
  overallPerformance,
  className,
  rangeGranularity,
}: {
  overallPerformance:
    | {
        Battery_Charge: number;
        Battery_Discharge: number;
        Consumed: number;
        Consumed_from_Grid: number;
        Fed_to_Grid: number;
        Revenue: number;
        Yield: number;
        revenueChart: { value: number; field: string; time: string }[];
      }
    | undefined;
  className?: string;
  rangeGranularity?: RangeGranularity;
}) {
  const { t } = useTranslation('translation', { keyPrefix: 'metrics' });
  const [yieldBarPercentage, consumedBarPercentage] = calculateBarsPercentage({
    left: overallPerformance?.Yield,
    right: overallPerformance?.Consumed,
  });

  const [fedToGridBarPercentage, consumedFromGridBarPercentage] = calculateBarsPercentage({
    left: overallPerformance?.Fed_to_Grid,
    right: overallPerformance?.Consumed_from_Grid,
  });

  const [batteryChargeBarPercentage, batteryDischargeBarPercentage] = calculateBarsPercentage({
    left: overallPerformance?.Battery_Charge,
    right: overallPerformance?.Battery_Discharge,
  });

  return (
    <div className={cn(className)}>
      {/*  Card*/}
      <div className="flex min-h-24 min-w-72 flex-col gap-1 rounded-lg border p-3">
        <div className="flex w-full justify-between">
          <div className="flex flex-col gap-3">
            <span className="text-sm text-muted-foreground">{t('pv_production')}</span>

            <div className="flex items-end gap-1.5">
              <span className="text-xl font-bold">
                {formatValueAndUnit(overallPerformance?.Yield).value}
              </span>
              <span className="text-muted-foreground">
                {formatValueAndUnit(overallPerformance?.Yield).unit}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3 text-end">
            <span className="text-sm text-muted-foreground">{t('consumption')}</span>

            <div className="flex items-end gap-1.5">
              <span className="text-xl font-bold">
                {formatValueAndUnit(overallPerformance?.Consumed).value}
              </span>
              <span className="text-muted-foreground">
                {formatValueAndUnit(overallPerformance?.Consumed).unit}
              </span>
            </div>
          </div>
        </div>

        <div className="flex w-full">
          <div
            className="h-1.5 rounded-l-lg"
            style={{
              width: `${yieldBarPercentage}%`,
              backgroundColor: METRIC_COLORS.PV_PRODUCTION,
            }}
          ></div>
          <div
            className="h-1.5 rounded-r-lg"
            style={{
              width: `${consumedBarPercentage}%`,
              backgroundColor: METRIC_COLORS.CONSUMED,
            }}
          ></div>
        </div>
      </div>

      {/*  Card*/}
      <div className="flex min-h-24 min-w-72 flex-col gap-1 rounded-lg border p-3">
        <div className="flex w-full justify-between">
          <div className="flex flex-col gap-3">
            <span className="text-sm text-muted-foreground">{t('fed_to_grid')}</span>

            <div className="flex items-end gap-1.5">
              <span className="text-xl font-bold">
                {formatValueAndUnit(overallPerformance?.Fed_to_Grid).value}
              </span>
              <span className="text-muted-foreground">
                {formatValueAndUnit(overallPerformance?.Fed_to_Grid).unit}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3 text-end">
            <span className="text-sm text-muted-foreground">{t('consumed_from_grid')}</span>

            <div className="flex items-end gap-1.5">
              <span className="text-xl font-bold">
                {formatValueAndUnit(overallPerformance?.Consumed_from_Grid).value}
              </span>
              <span className="text-muted-foreground">
                {formatValueAndUnit(overallPerformance?.Consumed_from_Grid).unit}
              </span>
            </div>
          </div>
        </div>

        <div className="flex w-full">
          <div
            className="h-1.5 w-[60%] rounded-l-lg"
            style={{
              width: `${fedToGridBarPercentage}%`,
              backgroundColor: METRIC_COLORS.FED_TO_GRID,
            }}
          ></div>
          <div
            className="h-1.5 w-[40%] rounded-r-lg"
            style={{
              width: `${consumedFromGridBarPercentage}%`,
              backgroundColor: METRIC_COLORS.CONSUMED_FROM_GRID,
            }}
          ></div>
        </div>
      </div>

      {/*  Card*/}
      <div className="flex min-h-24 min-w-72 flex-col gap-1 rounded-lg border p-3">
        <div className="flex w-full justify-between">
          <div className="flex flex-col gap-3">
            <span className="text-sm text-muted-foreground">{t('battery_charge')}</span>
            <div className="flex items-end gap-1.5">
              <span className="text-xl font-bold">
                {formatValueAndUnit(overallPerformance?.Battery_Charge).value}
              </span>
              <span className="text-muted-foreground">
                {formatValueAndUnit(overallPerformance?.Battery_Charge).unit}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3 text-end">
            <span className="text-sm text-muted-foreground">{t('battery_discharge')}</span>
            <div className="flex items-end gap-1.5">
              <span className="text-xl font-bold">
                {formatValueAndUnit(overallPerformance?.Battery_Discharge).value}
              </span>
              <span className="text-muted-foreground">
                {formatValueAndUnit(overallPerformance?.Battery_Discharge).unit}
              </span>
            </div>
          </div>
        </div>

        <div className="flex w-full">
          <div
            className="h-1.5rounded-l-lg"
            style={{
              width: `${batteryChargeBarPercentage}%`,
              backgroundColor: METRIC_COLORS.BATTERY_CHARGE,
            }}
          ></div>
          <div
            className="h-1.5 rounded-r-lg"
            style={{
              width: `${batteryDischargeBarPercentage}%`,
              backgroundColor: METRIC_COLORS.BATTERY_DISCHARGE,
            }}
          ></div>
        </div>
      </div>

      {/*  Card*/}
      <div className="flex flex-col gap-1 rounded-lg border p-3">
        <div className="flex w-full justify-between gap-2">
          <span className="text-nowrap text-sm text-[#7D7D85]">{t('revenue')}</span>
          <span className="text-nowrap text-xl font-bold">
            {overallPerformance?.Revenue}{' '}
            <span className="text-sm text-muted-foreground">€</span>
          </span>
        </div>
        <ResponsiveContainer width="100%" height={46}>
          <AreaChart
            data={overallPerformance?.revenueChart ?? []}
            margin={{
              top: 0,
              right: -5,
              left: -5,
              bottom: 1,
            }}
          >
            <Tooltip
              content={({ payload }: TooltipContentProps<ValueType, NameType>) => {
                const {
                  payload: { value, time },
                } = payload?.[0] || { payload: {} };

                let timeFormat = '';
                if (time) {
                  if (rangeGranularity === '5min' || rangeGranularity === 'hourly') {
                    timeFormat = format(time, 'HH:mm');
                  } else {
                    timeFormat = format(time, 'MMM d');
                  }
                }

                return (
                  <div className="border-1 flex flex-col gap-2 border-gray-100 bg-white p-4">
                    <span className="total text-sm">{timeFormat}</span>
                    <span className="text-[#5ebb3c]">{value} €</span>
                  </div>
                );
              }}
            />
            <Area type="monotone" dataKey="value" stroke="#b652ff50" fill="#b652ff90" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
